import createDom from '../dom/create.js';
import Component from './Component';

const error = function( ...args ) {
	if(console && console.error)
		console.error.apply(console, args);
	return null;
}


/**
 * Manage {@type Component} lifecycle
 * @author Geoffrey Mayeux
 * @category components
 */
class Components{
	/**
	 * Create a Components
	 * @param  {string} [selector] - The selector to in which to look for components
	 */
	constructor({ selector = 'html' } = {}){
		this.selector 	= selector;
		this.element 	= createDom(this.selector);
		this.registry  	= new Map();
		this.elements 	= new Map();
		this.initFns 	= ["init", "attach"];
		this.destroyFns = ["detach", "destroy"];
		this.creator    = (clazz, element) => new clazz(element);
	}

	/**
	 * Register components
	 * @param  {Object.<string, Component>} components - List of components to register
	 */
	register(components){
		this.registry = new Map([...this.registry].concat(Object.entries(components)));
	}

	/**
	 * Checks wheter a node needs a component to be setup
	 * @param  {HTMLDomElement} element - The node to be checked
	 * @returns {boolean} true when the node needs to be setup
	 */
	needSetup(element){
		return 'component' in element.dataset
			&& this.registry.has(element.dataset.component)
			&& !this.elements.has(element);
	}

	get(element){
		if(this.elements.has(element))
			return this.elements.get(element);
		return null;
	}

	/**
	 * Setup every known {@link Component} of this instance that haven't be created. Also destroys any removed {@link Component} from the dom.
	 *
	 * @param {function} argumentsCreator - Function that returns additional arguments to pass to component as array. Receive the dom element and component class as argument
	 * @returns {Component[]} The created components
	 */
	async setup(){
		await this.clean();
		let components = [this.element]
		.concat(...this.element.querySelectorAll('[data-component]'))
		.filter(c => this.needSetup(c))
		.map(c => this.create(c));

		for(let component of components){
			await this.call(component, this.initFns);
		}
		return components;
	}

	/**
	 * Create the {@link Component} defined on the given DOM element and set the replace the data-component value by the created instance.
	 * @param {HTMLDomElement} element The DOM element.
	 * @returns {Component} The created component.
	 */
	create(element){
		let name 		= element.dataset.component;

		if(!this.registry.has(name)) return error("Component not found: ", name);

		let clazz 		= this.registry.get(name);
		let component 	= this.creator(clazz, element);
		
		if(!component) return error("Component not created", name);
		
		this.elements.set(element, component);

		return component;
	}


	async call(component, fns){
	    if(component instanceof Component){
	        for(let fn of fns){
	            if(typeof component[fn] == "function")
	                await component[fn]();
	        }
	    }
	}

	/**
	 * Destroys any removed {@link Component} from the dom.
	 */
	async clean(){
		for await(let [element, component] of this.elements.entries()){
			if(!(element.compareDocumentPosition(document.body) & Node.DOCUMENT_POSITION_CONTAINS)){
				await this.call(component, this.destroyFns);
				this.elements.delete(element);
			}
		}
	}
}

export default Components;
export { Components };